import { type FC, memo, type MouseEvent, type MouseEventHandler, type SyntheticEvent, useCallback } from 'react';
import { Box, type BoxProps, Typography, useColorScheme } from '@mui/joy';

import Icon, { type IconProps } from 'ui/Icon';

export interface InfoButtonProps extends Omit<BoxProps, 'onClick'> {
  label: string;
  iconFamily?: NonNullable<IconProps['family']>;
  iconName: string;
  description: string;
  onClick?: (event: MouseEvent<Element, globalThis.MouseEvent>, itemId: string) => void;
}

const InfoButton: FC<InfoButtonProps> = (props) => {
  const { label, iconFamily, iconName, description, onClick, ...rest } = props;
  const { mode } = useColorScheme();

  const handleClick = useCallback<MouseEventHandler>(
    (event) => {
      const itemId = event.currentTarget.getAttribute('itemId');
      if (!itemId) {
        return;
      }
      onClick?.(event, itemId);
    },
    [onClick],
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      component="button"
      gap={1}
      bgcolor="var(--info-button-color)"
      p={1.5}
      borderRadius="var(--joy-radius-lg)"
      onClick={handleClick}
      textAlign="left"
      {...rest}
      sx={{
        cursor: 'pointer',
        transition: 'background-color ease 200ms, box-shadow ease 200ms',
        '--info-button-color': 'var(--joy-palette-background-body)',
        '--info-label-color': 'var(--joy-palette-background-level1)',
        '--info-border-color':
          mode === 'dark' ? 'transparent' : 'color-mix(in srgb, var(--joy-palette-background-level1) 85%, var(--joy-palette-background-body) 15%)',
        '&:hover': {
          '--info-button-color': `
            ${
              mode === 'dark'
                ? 'var(--joy-palette-common-black)'
                : 'color-mix(in srgb, var(--joy-palette-background-level1) 0%, var(--joy-palette-background-body) 100%)'
            }
          `,
          '--info-label-color': `
            ${
              mode === 'dark'
                ? 'var(--joy-palette-background-level1)'
                : 'color-mix(in srgb, var(--joy-palette-background-level2) 0%, var(--joy-palette-background-level1) 100%)'
            }
          `,
          boxShadow: '0 1px 1px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.1), 0 0 0 1px var(--info-border-color)',
        },
        ...rest.sx,
      }}
    >
      <Box display="flex" gap={1} alignItems="center">
        <Box
          display="flex"
          flexDirection="column"
          width="2rem"
          height="2rem"
          justifyContent="center"
          alignItems="center"
          bgcolor="var(--info-label-color)"
          borderRadius="1rem"
          sx={{
            transition: 'background-color ease 200ms',
          }}
        >
          <Icon family={iconFamily as any} name={iconName} fw weight="light" color="primary" />
        </Box>
        <Typography fontSize={13} fontWeight={400}>
          {label}
        </Typography>
      </Box>
      <Typography fontSize={12} fontWeight={400} sx={{ opacity: 0.5, textWrap: 'balance' }}>
        {description}
      </Typography>
    </Box>
  );
};
// <i class="fa-kit fa-light-file-pen"></i>
export default memo(InfoButton);
